import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const idempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
const reasonCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)
const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey,
  sessionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
}

export const inventoryCountFreezeV1Schema = z
  .object({
    ...base,
    expectedVersion: z.literal(0),
    revisionId: entityIdSchema,
    locationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    countKind: z.enum(["cycle", "physical"]),
    blindEntry: z.boolean(),
    reasonCode,
  })
  .strict()

export const inventoryCountEnterV1Schema = z
  .object({
    ...base,
    expectedVersion: z.literal(1),
    revisionId: entityIdSchema,
    countedQuantityMilli: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    reasonCode,
  })
  .strict()

export const inventoryCountReviewV1Schema = z
  .object({
    ...base,
    expectedVersion: z.literal(2),
    revisionId: entityIdSchema,
    reasonCode,
  })
  .strict()

export const inventoryCountApproveV1Schema = z
  .object({
    ...base,
    expectedVersion: z.literal(3),
    revisionId: entityIdSchema,
    postingDate: z.string().date(),
    reasonCode,
  })
  .strict()

export const inventoryCountResultV1Schema = z
  .object({
    entityType: z.literal("inventory_count"),
    entityId: entityIdSchema,
    state: z.enum([
      "frozen",
      "variance_review",
      "pending_approval",
      "adjusted",
      "closed",
    ]),
    version: z.number().int().positive(),
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    locationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    countKind: z.enum(["cycle", "physical"]),
    blindEntry: z.boolean(),
    snapshotQuantityMilli: z.number().int().nullable(),
    countedQuantityMilli: z.number().int().nonnegative().nullable(),
    varianceQuantityMilli: z.number().int().nullable(),
    stockReconciliation: z.string().min(1).max(140).nullable(),
  })
  .strict()

export type InventoryCountFreezeV1 = z.infer<typeof inventoryCountFreezeV1Schema>
export type InventoryCountEnterV1 = z.infer<typeof inventoryCountEnterV1Schema>
export type InventoryCountReviewV1 = z.infer<typeof inventoryCountReviewV1Schema>
export type InventoryCountApproveV1 = z.infer<typeof inventoryCountApproveV1Schema>
export type InventoryCountResultV1 = z.infer<typeof inventoryCountResultV1Schema>
